/*
  Loyalty Bundler
  ---------------
  Drives sections/fc-loyalty-bundler.liquid. Forked from product-bundler.js,
  with the subscription and tier-card paths removed and step 2 re-pitched
  around a single goal quantity (10 bags at 50% off).

  Two things in here are load-bearing and must not drift:

    * getDiscountedUnitPrice() reproduces how Shopify actually charges a
      percentage discount — applied to the UNIT price and TRUNCATED to the
      cent, then multiplied by quantity. $39.99 at 50% off is a $19.995
      discount, truncated to $19.99, so $20.00/bag and $200.00 for ten.
      Rounding to the nearest cent instead gives $19.99/bag and $199.90,
      which is NOT what checkout charges. The same rule lives in
      product-bundler.js; changing one means changing both.

    * LADDER must match the store's automatic discounts in admin. This file
      only quotes prices — the money comes off at checkout. If the 10-bag
      automatic discount is edited, disabled or expires, this page keeps
      quoting 50% and checkout keeps charging full ladder price.
*/
(function () {
  'use strict';

  class LoyaltyBundler {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;

      const configEl = container.querySelector('[data-bundler-config]');
      if (!configEl) {
        console.error('LoyaltyBundler: Config element not found');
        return;
      }
      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('LoyaltyBundler: Failed to parse config', e);
        return;
      }

      this.currentStep = 1;
      this.selectedFlavors = new Set();
      this.customQuantities = {};
      this.isAddingToCart = false;
      this.quantitiesSeeded = false;
      this.goal = this.config.goalQuantity || 10;
      this.freeShippingFrom = this.config.freeShippingFrom || 4;

      // Rungs the progress bar draws, low to high. The liquid renders one
      // segment per rung in this same order.
      this.LADDER = [4, 6, 8, this.goal];

      this.config.variants.forEach(v => { this.customQuantities[v.id] = 0; });

      try {
        this.cacheElements();
        this.bindEvents();
        this.init();
      } catch (e) {
        console.error('LoyaltyBundler: Initialization error', e);
      }
    }

    cacheElements() {
      this.stepLinks = this.container.querySelectorAll('[data-step-link]');
      this.stepContents = this.container.querySelectorAll('[data-step]');
      this.presetCards = this.container.querySelectorAll('[data-select-preset]');
      this.flavorCheckboxes = this.container.querySelectorAll('.bundler__flavor-checkbox');
      this.hintText = this.container.querySelector('[data-hint]');
      this.continueButton = this.container.querySelector('[data-continue-step2]');
      this.errorMessage = this.container.querySelector('[data-error-message]');

      this.customFlavors = this.container.querySelectorAll('.bundler__custom-flavor');
      this.customAddButton = this.container.querySelector('[data-add-custom]');
      this.customTotalOriginal = this.container.querySelector('[data-custom-total-original]');
      this.customTotalDiscounted = this.container.querySelector('[data-custom-total-discounted]');
      this.customTotalRow = this.container.querySelector('[data-custom-total]');
      this.customTotalSavings = this.container.querySelector('[data-custom-total-savings]');
      this.customBags = this.container.querySelector('[data-custom-bags]');

      this.progressMessage = this.container.querySelector('[data-progress-message]');
      this.progressSegments = [];
      this.progressFills = [];
      this.progressCheckmarks = [];
      this.progressLabels = [];
      for (let i = 1; i <= this.LADDER.length; i++) {
        this.progressSegments.push(this.container.querySelector('[data-progress-segment="' + i + '"]'));
        this.progressFills.push(this.container.querySelector('[data-progress-fill-' + i + ']'));
        this.progressCheckmarks.push(this.container.querySelector('[data-progress-checkmark-' + i + ']'));
        this.progressLabels.push(this.container.querySelector('[data-progress-label-' + i + ']'));
      }

      this.customEyebrow = this.container.querySelector('[data-custom-eyebrow]');
      this.eyebrowDiscount = this.container.querySelector('[data-eyebrow-discount]');
      this.eyebrowDiscountText = this.container.querySelector('[data-eyebrow-discount-text]');
      this.eyebrowShipping = this.container.querySelector('[data-eyebrow-shipping]');
      this.customNotices = this.container.querySelector('[data-custom-notices]');
      this.noticeShipping = this.container.querySelector('[data-notice-shipping]');
      this.noticeDiscount = this.container.querySelector('[data-notice-discount]');
      this.customCtaWrapper = this.container.querySelector('[data-custom-cta-wrapper]');
      this.customCta = this.container.querySelector('[data-custom-cta]');

      this.calloutWrapper = this.container.querySelector('[data-callout-wrapper]');
      this.calloutGet = this.container.querySelector('[data-callout-get]');

      this.stickyButton = this.container.querySelector('[data-sticky-button]');
      this.stickyAction = this.container.querySelector('[data-sticky-action]');
      this.stickyText = this.container.querySelector('[data-sticky-text]');
      this.focusOverlay = document.querySelector('[data-bundler-overlay]');
    }

    // ---------------------------------------------------------------- events

    bindEvents() {
      this.stepLinks.forEach(link => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          const step = parseInt(link.dataset.stepLink);
          if (step === 1 || (step === 2 && this.selectedFlavors.size > 0)) this.goToStep(step);
        });
      });

      this.presetCards.forEach(card => {
        card.addEventListener('click', () => {
          const preset = card.closest('.bundler__preset');
          const button = card.querySelector('.bundler__preset-button');
          this.selectPreset(preset.dataset.variants, button || card);
        });
      });

      this.flavorCheckboxes.forEach(checkbox => {
        checkbox.addEventListener('change', () => this.updateFlavorSelection());
      });

      if (this.continueButton) {
        this.continueButton.addEventListener('click', () => {
          if (this.selectedFlavors.size > 0) {
            this.hideErrorMessage();
            this.goToStep(2);
          } else {
            this.showErrorMessage();
          }
        });
        this.continueButton.addEventListener('mouseenter', () => {
          if (this.selectedFlavors.size === 0) this.showErrorMessage();
        });
        this.continueButton.addEventListener('mouseleave', () => this.hideErrorMessage());
      }

      // On mobile the plain `click` on these +/- buttons is unreliable — a tap
      // can land while the fixed sticky bar / mobile URL bar repositions the
      // viewport, and the browser then drops the synthesized click, so it takes
      // more than one tap to change the number. Fire on pointerup (the real
      // release) for touch, and swallow the single ghost click that same tap
      // generates so one tap = one step. Mouse/keyboard still go through click.
      const bindQtyTap = (btn, handler) => {
        if (!btn) return;
        let swallowClick = false;
        btn.addEventListener('pointerup', (e) => {
          if (e.pointerType === 'mouse') return;
          swallowClick = true;
          handler();
        });
        btn.addEventListener('click', () => {
          if (swallowClick) { swallowClick = false; return; }
          handler();
        });
      };

      this.customFlavors.forEach(flavorEl => {
        const variantId = parseInt(flavorEl.dataset.variantId);
        bindQtyTap(flavorEl.querySelector('[data-qty-minus]'), () => this.updateCustomQuantity(variantId, -1));
        bindQtyTap(flavorEl.querySelector('[data-qty-plus]'), () => this.updateCustomQuantity(variantId, 1));
      });

      if (this.customAddButton) {
        this.customAddButton.addEventListener('click', () => this.addCustomToCart());
      }

      if (this.stickyAction) {
        // Activate on pointerup (the actual touch release), not just click. This
        // bar is position:fixed and repositions on visualViewport changes (mobile
        // URL bar show/hide); a real tap can trigger that reposition and the
        // browser then drops the synthesized click, so the first tap appears to
        // do nothing. scrollToSection() is idempotent, but de-dupe anyway.
        let lastFire = 0;
        const fireScroll = () => {
          const now = (window.performance && performance.now) ? performance.now() : Date.now();
          if (now - lastFire < 500) return;
          lastFire = now;
          this.scrollToSection();
        };
        let px = 0, py = 0, moved = false;
        this.stickyAction.addEventListener('pointerdown', (e) => { px = e.clientX; py = e.clientY; moved = false; }, { passive: true });
        this.stickyAction.addEventListener('pointermove', (e) => {
          if (Math.abs(e.clientX - px) > 10 || Math.abs(e.clientY - py) > 10) moved = true;
        }, { passive: true });
        this.stickyAction.addEventListener('pointerup', () => { if (!moved) fireScroll(); });
        this.stickyAction.addEventListener('click', () => fireScroll());
      }

      this.setupStickyObserver();

      window.addEventListener('pageshow', (e) => {
        this.isAddingToCart = false;
        this.clearAllLoadingStates();
        this.syncSelectedFlavorsFromCheckboxes();
        this.updateContinueButton();

        if (e.persisted) {
          // bfcache restore (e.g. browser Back from checkout): the theme's cart
          // refresh is gated behind `alpine:init`, which does NOT fire on a
          // restored page, so the mini-cart keeps its stale pre-add state.
          // Trigger the theme's reload directly to re-sync it with reality.
          try {
            if (window.Alpine && Alpine.store && Alpine.store('xMiniCart') &&
                typeof Alpine.store('xMiniCart').reLoad === 'function') {
              Alpine.store('xMiniCart').reLoad();
            }
          } catch (err) { /* theme cart store absent — nothing to resync */ }

          if (history.state && history.state.bundlerStep !== undefined) {
            this.goToStep(history.state.bundlerStep, true, true, true);
          } else {
            this.reset();
          }
        }
      });

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.isAddingToCart = false;
          this.clearAllLoadingStates();
          this.syncSelectedFlavorsFromCheckboxes();
          this.updateContinueButton();
        }
      });

      window.addEventListener('popstate', (e) => {
        if (e.state && e.state.bundlerStep !== undefined) {
          this.goToStep(e.state.bundlerStep, false, true);
        } else if (this.currentStep === 2) {
          this.goToStep(1, false, true);
        }
      });

      this.bindSwipeBack();
    }

    init() {
      this.selectAllFlavorsIfFreshLoad();
      this.updateHintVisibility();
      this.updateContinueButton();

      if (!history.state || history.state.bundlerStep === undefined) {
        history.replaceState({ bundlerStep: 1 }, '', window.location.href);
      } else if (history.state.bundlerStep === 2) {
        // Returning to the page (e.g. back from checkout after a full load): the
        // history entry still says step 2, but the page renders step 1 while the
        // browser restores the taller step-2 scroll offset — landing the visitor
        // far down the page. Restore step 2 ourselves and pin to the top instead.
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
        this.goToStep(2, true, true, true);
      }
    }

    selectAllFlavorsIfFreshLoad() {
      const anyChecked = Array.from(this.flavorCheckboxes).some(cb => cb.checked);
      if (anyChecked) return;
      this.flavorCheckboxes.forEach(checkbox => {
        if (!checkbox.disabled) {
          checkbox.checked = true;
          this.selectedFlavors.add(parseInt(checkbox.value));
        }
      });
    }

    reset() {
      this.currentStep = 1;
      this.quantitiesSeeded = false;
      this.isAddingToCart = false;
      this.clearAllLoadingStates();
      this.goToStep(1, true, true, true);
    }

    clearAllLoadingStates() {
      this.container.querySelectorAll('.is-loading').forEach(el => el.classList.remove('is-loading'));
    }

    // ------------------------------------------------------------ navigation

    // Mobile: a left-to-right swipe anywhere on step 2 goes back to step 1, the
    // way an OS back gesture reads. Only a deliberate, mostly-horizontal drag
    // counts — once the finger has drifted far enough vertically we call it a
    // page scroll and stop watching. Swipes that start hard against the left edge
    // are left alone, because the browser's own back gesture owns that strip.
    bindSwipeBack() {
      const stepTwo = this.container.querySelector('[data-step="2"]');
      if (!stepTwo) return;
      if (window.matchMedia && !window.matchMedia('(pointer: coarse)').matches) return;

      const EDGE_GUARD = 24; // px of left edge the browser gesture owns
      const MIN_X = 70;      // px the finger must travel right to count
      const MAX_Y = 50;      // px of vertical drift before it's a scroll
      let startX = 0, startY = 0, tracking = false;

      stepTwo.addEventListener('touchstart', (e) => {
        if (e.touches.length !== 1) { tracking = false; return; }
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        tracking = startX > EDGE_GUARD;
      }, { passive: true });

      stepTwo.addEventListener('touchmove', (e) => {
        if (!tracking) return;
        if (e.touches.length !== 1) { tracking = false; return; }
        if (Math.abs(e.touches[0].clientY - startY) > MAX_Y) tracking = false;
      }, { passive: true });

      stepTwo.addEventListener('touchcancel', () => { tracking = false; }, { passive: true });

      stepTwo.addEventListener('touchend', (e) => {
        if (!tracking) return;
        tracking = false;
        if (this.currentStep !== 2) return;
        const t = e.changedTouches[0];
        if (!t) return;
        if (t.clientX - startX < MIN_X) return;
        if (Math.abs(t.clientY - startY) > MAX_Y) return;
        // Pin the top of the bundler in view BEFORE the panels swap. The crumb
        // sits at the top, so tapping it means you are already there and the
        // slide plays in view; a swipe fires wherever you are, usually far down
        // a step 2 that is much taller than step 1. Without this the panels swap,
        // the container collapses to step-1 height, and the whole slide plays off
        // the top of the screen while a smooth scroll crawls up to meet it.
        this.scrollToSection();
        // Same route as tapping the step-1 crumb, so the two stay in sync; the
        // instant flag stops goToStep's trailing smooth scroll re-running it.
        this.goToStep(1, false, false, true);
      }, { passive: true });
    }

    goToStep(step, skipAnimation = false, fromPopState = false, instantScroll = false) {
      const previousStep = this.currentStep;
      this.currentStep = step;

      this.clearAllLoadingStates();

      if (!fromPopState && step === 2 && previousStep === 1) {
        history.pushState({ bundlerStep: 2 }, '', window.location.href);
      }

      this.stepLinks.forEach(link => {
        const linkStep = parseInt(link.dataset.stepLink);
        link.classList.toggle('is-active', linkStep === step);
        link.classList.toggle('is-completed', linkStep < step);
      });

      if (skipAnimation) {
        this.stepContents.forEach(content => {
          content.classList.remove('is-sliding-out-left', 'is-sliding-out-right', 'is-sliding-in-left', 'is-sliding-in-right');
          content.classList.toggle('is-active', parseInt(content.dataset.step) === step);
        });
      } else {
        const currentContent = this.container.querySelector('[data-step="' + previousStep + '"]');
        const nextContent = this.container.querySelector('[data-step="' + step + '"]');

        if (currentContent && nextContent && previousStep !== step) {
          const goingForward = step > previousStep;

          // Park the incoming panel off-screen on the side it should enter from,
          // with the transition switched OFF. This matters: the base rule gives
          // every inactive panel BOTH `transform: translateX(100%)` and a live
          // `transition: transform .3s`, so an idle step 1 sits off to the RIGHT
          // and merely adding `is-sliding-in-left` animates it leftward instead of
          // teleporting it. A forced reflow flushes layout but does not cancel a
          // running transition, so a frame later we would ask it to travel to 0
          // from a position still hard against the right edge — a back transition
          // that slides in from the wrong side. Killing the transition first makes
          // the start position instant, so only the real move is animated.
          nextContent.classList.remove('is-active');
          nextContent.style.transition = 'none';
          nextContent.classList.add(goingForward ? 'is-sliding-in-right' : 'is-sliding-in-left');
          nextContent.offsetHeight;          // commit the off-screen start position

          // Hand the transition back to the stylesheet and commit that too, so the
          // move to 0 below is the first thing the restored transition sees.
          nextContent.style.transition = '';
          nextContent.offsetHeight;

          // Animate out current, animate in next
          currentContent.classList.add(goingForward ? 'is-sliding-out-left' : 'is-sliding-out-right');
          currentContent.classList.remove('is-active');
          nextContent.classList.remove('is-sliding-in-right', 'is-sliding-in-left');
          nextContent.classList.add('is-active');

          // Clean up once the slide is done. Same trick: drop the transition while
          // the outgoing panel snaps back to its parked state, or it spends another
          // 300ms invisibly travelling across the section.
          setTimeout(() => {
            currentContent.style.transition = 'none';
            currentContent.classList.remove('is-sliding-out-left', 'is-sliding-out-right');
            currentContent.offsetHeight;
            currentContent.style.transition = '';
          }, 300);
        }
      }

      if (step === 1) {
        this.updateContinueButton();
        this.updateHintVisibility();
      }

      this.updateStickyText();

      if (step === 2) {
        this.seedQuantitiesFromSelection();
        this.revealCallout();
        this.updateProgressBar();
      }

      this.container.scrollIntoView({ behavior: instantScroll ? 'auto' : 'smooth', block: 'start' });
    }

    scrollToSection() {
      this.container.scrollIntoView({ behavior: 'instant', block: 'start' });
    }

    updateStickyText() {
      if (this.stickyText) {
        this.stickyText.textContent = this.currentStep === 1 ? this.config.stickyStep1Text : this.config.stickyStep2Text;
      }
    }

    // ------------------------------------------------------- step 1 selection

    selectPreset(variantsString, buttonElement) {
      const variantNames = variantsString.split(',').map(s => s.trim());
      this.selectedFlavors.clear();
      this.flavorCheckboxes.forEach(cb => { cb.checked = false; });
      variantNames.forEach(name => {
        const variant = this.config.variants.find(v => v.title.toLowerCase() === name.toLowerCase());
        if (variant) {
          this.selectedFlavors.add(variant.id);
          const checkbox = this.container.querySelector('.bundler__flavor-checkbox[value="' + variant.id + '"]');
          if (checkbox) checkbox.checked = true;
        }
      });
      this.quantitiesSeeded = false; // a new pick re-spreads the goal
      this.updateHintVisibility();
      this.updateContinueButton();
      setTimeout(() => this.goToStep(2), 150);
    }

    syncSelectedFlavorsFromCheckboxes() {
      this.selectedFlavors.clear();
      this.flavorCheckboxes.forEach(checkbox => {
        if (checkbox.checked) this.selectedFlavors.add(parseInt(checkbox.value));
      });
    }

    updateFlavorSelection() {
      this.selectedFlavors.clear();
      this.flavorCheckboxes.forEach(checkbox => {
        if (checkbox.checked) this.selectedFlavors.add(parseInt(checkbox.value));
      });
      this.quantitiesSeeded = false; // changing the picks re-spreads the goal
      this.updateHintVisibility();
      this.updateContinueButton();
      if (this.selectedFlavors.size > 0) this.hideErrorMessage();
    }

    updateHintVisibility() {
      if (this.hintText) this.hintText.classList.toggle('is-visible', this.selectedFlavors.size === 1);
    }

    updateContinueButton() {
      if (this.continueButton) this.continueButton.classList.toggle('is-disabled', this.selectedFlavors.size === 0);
    }

    showErrorMessage() {
      if (!this.errorMessage) return;
      this.errorMessage.classList.add('is-visible');
      if (this.errorTimeout) clearTimeout(this.errorTimeout);
      this.errorTimeout = setTimeout(() => this.hideErrorMessage(), 5000);
    }

    hideErrorMessage() {
      if (!this.errorMessage) return;
      this.errorMessage.classList.remove('is-visible');
      if (this.errorTimeout) {
        clearTimeout(this.errorTimeout);
        this.errorTimeout = null;
      }
    }

    // ------------------------------------------------------------- quantities

    /*
      Spread the goal quantity across the flavors picked on step 1.

      Every flavor gets floor(goal / count); the remainder goes one bag each to
      the flavors at the front of the list, in the order they appear on the
      product. So with a goal of 10: one flavor takes all 10, two split 5/5, and
      three land 4/3/3 — Vanilla, Chocolate, Strawberry.

      Seeded once per selection. Coming back to step 2 after nudging the +/-
      buttons keeps the shopper's numbers rather than resetting their work;
      changing the flavor picks clears the flag so the goal is re-spread.
    */
    seedQuantitiesFromSelection() {
      if (this.quantitiesSeeded) return;

      const selected = this.config.variants.filter(v => this.selectedFlavors.has(v.id) && v.available);
      const targets = selected.length > 0
        ? selected
        : this.config.variants.filter(v => v.available);

      this.config.variants.forEach(v => { this.customQuantities[v.id] = 0; });

      const distribution = this.getDistribution(targets.length, this.goal);
      targets.forEach((variant, i) => {
        this.customQuantities[variant.id] = distribution[i] || 0;
      });

      this.quantitiesSeeded = true;
      this.updateCustomQuantityDisplays();
    }

    getDistribution(flavorCount, totalQuantity) {
      if (flavorCount <= 0) return [totalQuantity];
      const perFlavor = Math.floor(totalQuantity / flavorCount);
      const remainder = totalQuantity % flavorCount;
      const distribution = Array(flavorCount).fill(perFlavor);
      for (let i = 0; i < remainder; i++) distribution[i]++;
      return distribution;
    }

    updateCustomQuantity(variantId, delta) {
      this.customQuantities[variantId] = Math.max(0, (this.customQuantities[variantId] || 0) + delta);
      this.updateCustomQuantityDisplays();
      this.updateProgressBar();
    }

    updateCustomQuantityDisplays() {
      this.customFlavors.forEach(flavorEl => {
        const variantId = parseInt(flavorEl.dataset.variantId);
        const qty = this.customQuantities[variantId] || 0;
        const valueEl = flavorEl.querySelector('[data-qty-value]');
        if (valueEl) valueEl.textContent = qty;
        flavorEl.classList.toggle('is-selected', qty > 0);
      });
    }

    getTotalCustomQuantity() {
      return Object.values(this.customQuantities).reduce((sum, qty) => sum + qty, 0);
    }

    // ---------------------------------------------------------------- pricing

    // Threshold-based: the highest rung at or below the total applies, so 11
    // bags resolve to the same 50% as 10.
    getThresholdDiscount(total, map) {
      let discount = 0;
      const keys = Object.keys(map).map(Number).sort((a, b) => b - a);
      for (const qty of keys) {
        if (total >= qty) { discount = map[qty]; break; }
      }
      return discount;
    }

    getOnetimeDiscount(total) {
      return this.getThresholdDiscount(total, this.config.discounts);
    }

    // See the note at the top of this file: Shopify truncates the per-unit
    // discount to the cent before multiplying by quantity.
    getDiscountedUnitPrice(basePrice, discountPercent) {
      // toFixed(6) first so float noise can't drop the value below a whole cent.
      const unitDiscount = Math.floor(Number((basePrice * discountPercent / 100).toFixed(6)));
      return basePrice - unitDiscount;
    }

    formatMoney(cents) {
      // Snap to a whole cent first (kills float noise like 16199.999999999996),
      // then drop the decimals only when the amount IS a whole dollar. These are
      // compared against the cart, so they must never be truncated.
      const whole = Math.round(cents);
      if (whole % 100 === 0) return '$' + (whole / 100);
      return '$' + (whole / 100).toFixed(2);
    }

    // ----------------------------------------------------------- step 2 state

    updateProgressBar() {
      const total = this.getTotalCustomQuantity();
      const d = this.config.discounts;
      const goalDiscount = d[this.goal] || 50;

      let message = '';
      if (total === 0) {
        message = 'Add ' + this.goal + ' bags for <strong>' + goalDiscount + '% off!</strong>';
      } else if (total < this.goal) {
        const needed = this.goal - total;
        message = 'Add ' + needed + ' more bag' + (needed !== 1 ? 's' : '') +
                  ' for <strong>' + goalDiscount + '% off!</strong>';
      } else {
        message = '<strong>' + goalDiscount + '% off + free shipping</strong> unlocked!';
      }

      if (this.progressMessage) this.progressMessage.innerHTML = message;

      this.applyProgressSegments(total, this.LADDER, this.progressFills, this.progressSegments, this.progressCheckmarks);

      this.updateCustomTotal(total);
      this.updateCustomEyebrow(total);
      this.updateCustomNotices(total);
      this.updateCallout(total);
    }

    // The "GET X BAGS FREE" callout above the builder. It only shows once the
    // builder holds the goal (10+ bags), and describes the bundle in hand.
    // Same math as the PDP bundler's getBogoText: bags paid for = quantity at
    // the discounted price, rounded to nearest. While step 2's arrival reveal
    // is pending, it leaves showing and hiding to that.
    updateCallout(total) {
      if (!this.calloutWrapper || !this.calloutGet) return;
      const discountPercent = this.getOnetimeDiscount(total);
      const paid = Math.max(1, Math.round(total * (100 - discountPercent) / 100));
      const free = total - paid;
      this.calloutGet.textContent = free > 0
        ? 'Get ' + free + ' bag' + (free !== 1 ? 's' : '') + ' free'
        : 'Get ' + discountPercent + '% off';
      if (this.currentStep === 2 && !this.calloutTimer) {
        this.calloutWrapper.classList.toggle('is-animated', total >= this.goal);
      }
    }

    // Arriving on step 2 with the goal already met (seeded from step 1), the
    // callout pops in just after the step finishes sliding in rather than
    // mid-slide. The PDP waits for its first tier card to scroll into view;
    // here the builder sits right under the heading, so arrival is the moment.
    revealCallout() {
      if (!this.calloutWrapper) return;
      this.calloutWrapper.classList.remove('is-animated');
      clearTimeout(this.calloutTimer);
      this.calloutTimer = setTimeout(() => {
        this.calloutTimer = null;
        this.updateCallout(this.getTotalCustomQuantity());
      }, 350);
    }

    applyProgressSegments(total, thresholds, fills, segments, checkmarks) {
      thresholds.forEach((target, i) => {
        const prev = i === 0 ? 0 : thresholds[i - 1];
        const fill = Math.max(0, Math.min(1, (total - prev) / (target - prev))) * 100;
        const complete = total >= target;
        if (fills[i]) fills[i].style.width = fill + '%';
        if (segments[i]) {
          // Fire the unlock animation only on the transition into completion,
          // not on every re-render — otherwise it replays on unrelated updates.
          const wasComplete = segments[i].classList.contains('is-complete');
          segments[i].classList.toggle('is-complete', complete);
          if (complete && !wasComplete) {
            const seg = segments[i];
            seg.classList.remove('just-unlocked');
            void seg.offsetWidth; // reflow, so the animation restarts cleanly
            seg.classList.add('just-unlocked');
            setTimeout(() => seg.classList.remove('just-unlocked'), 900);
          }
        }
        if (checkmarks[i]) checkmarks[i].classList.toggle('is-visible', complete);
      });
    }

    updateCustomTotal(total) {
      if (!this.customTotalOriginal || !this.customTotalDiscounted) return;

      const basePrice = this.config.variants[0] ? this.config.variants[0].price : 0;
      const discountPercent = this.getOnetimeDiscount(total);
      const originalTotal = basePrice * total;
      const discountedTotal = this.getDiscountedUnitPrice(basePrice, discountPercent) * total;

      this.customTotalOriginal.textContent = total > 0 ? this.formatMoney(originalTotal) : '';
      this.customTotalDiscounted.textContent = total > 0 ? this.formatMoney(discountedTotal) : '';
      // Savings is taken from the two ROUNDED totals, not the raw floats, so
      // the three numbers on the line always agree when a shopper checks them.
      const savings = Math.round(originalTotal) - Math.round(discountedTotal);
      if (this.customTotalSavings) {
        this.customTotalSavings.textContent = savings > 0 ? `SAVE ${this.formatMoney(savings)}` : '';
      }
      // The struck price and the chip both hang off this class.
      if (this.customTotalRow) {
        this.customTotalRow.classList.toggle('has-savings', total > 0 && savings > 0);
      }
      if (this.customBags) {
        this.customBags.textContent = total > 0 ? total + ' bag' + (total !== 1 ? 's' : '') : '';
      }
    }

    updateCustomEyebrow(total) {
      const discountPercent = this.getOnetimeDiscount(total);
      const showDiscount = total >= 1 && discountPercent > 0;
      const showShipping = total >= this.freeShippingFrom;

      if (this.eyebrowDiscountText && showDiscount) {
        this.eyebrowDiscountText.textContent = discountPercent + '% OFF';
      }
      if (this.eyebrowDiscount) this.eyebrowDiscount.classList.toggle('is-visible', showDiscount);
      if (this.eyebrowShipping) this.eyebrowShipping.classList.toggle('is-visible', showShipping);
      if (this.customEyebrow) {
        this.customEyebrow.classList.toggle('is-visible', showDiscount || showShipping);
        this.customEyebrow.classList.toggle('is-complete', showDiscount && showShipping);
      }
    }

    updateCustomNotices(total) {
      const firstRung = Math.min.apply(null, Object.keys(this.config.discounts).map(Number));
      const showDiscountNotice = total >= 1 && total < firstRung;
      const showShippingNotice = total >= 1 && total < this.freeShippingFrom;

      let ctaText = '';
      if (total >= 1 && total < this.goal) {
        const needed = this.goal - total;
        const goalDiscount = this.config.discounts[this.goal] || 50;
        ctaText = 'Add ' + needed + ' more bag' + (needed !== 1 ? 's' : '') +
                  ' to get your ' + goalDiscount + '% off!';
      }

      if (this.noticeShipping) this.noticeShipping.classList.toggle('is-visible', showShippingNotice);
      if (this.noticeDiscount) this.noticeDiscount.classList.toggle('is-visible', showDiscountNotice);
      if (this.customNotices) this.customNotices.classList.toggle('is-visible', showShippingNotice || showDiscountNotice);
      if (this.customCta) this.customCta.textContent = ctaText;
      if (this.customCtaWrapper) this.customCtaWrapper.classList.toggle('is-visible', ctaText !== '');
    }

    // ------------------------------------------------------------------- cart

    async addCustomToCart() {
      // Build in on-screen order (config.variants == the order flavors are
      // listed in the builder) so the cart and checkout match what's shown.
      const items = this.config.variants
        .filter(v => (this.customQuantities[v.id] || 0) > 0)
        .map(v => ({ id: v.id, quantity: this.customQuantities[v.id] }));
      if (items.length === 0) return;
      await this.updateCart(items, this.customAddButton);
    }

    async updateCart(items, buttonElement) {
      if (this.isAddingToCart) return;
      this.isAddingToCart = true;
      if (buttonElement) buttonElement.classList.add('is-loading');

      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        const productLineItems = cart.items.filter(item => item.product_id === this.config.productId);
        if (productLineItems.length > 0) {
          const updates = {};
          productLineItems.forEach(item => { updates[item.key] = 0; });
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates })
          });
        }

        if (this.config.bonusProducts && this.config.bonusProducts.length > 0) {
          this.config.bonusProducts.forEach(bonus => {
            if (bonus.variantId) items.push({ id: bonus.variantId, quantity: bonus.quantity || 1 });
          });
        }

        // Shopify adds each new line at the TOP of the cart, so adding in list
        // order would leave cart.items (and therefore checkout) reversed. Add in
        // REVERSE list order so the final cart ends up in on-screen builder
        // order — flavors in display order, then any bonus products. Sequential
        // (one line per request) because a single multi-item /cart/add.js isn't
        // deterministic. Item count is small (one per flavor, plus bonuses).
        for (const item of items.slice().reverse()) {
          const addResponse = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [item] })
          });

          if (!addResponse.ok) {
            const responseText = await addResponse.text();
            this.isAddingToCart = false;
            if (buttonElement) buttonElement.classList.remove('is-loading');
            alert('Error adding to cart: ' + responseText);
            return;
          }
        }

        window.location.href = '/checkout';
      } catch (error) {
        this.isAddingToCart = false;
        if (buttonElement) buttonElement.classList.remove('is-loading');
        alert('Error: ' + error.message);
      }
    }

    // ----------------------------------------------------------- sticky bar

    setupStickyObserver() {
      if (!this.stickyButton || !('IntersectionObserver' in window)) return;

      let bundlerInView = false;
      const visibleCtas = new Set();

      const updateVisibility = () => {
        this.stickyButton.classList.toggle('is-visible', !bundlerInView && visibleCtas.size === 0);
      };

      const bundlerObserver = new IntersectionObserver((entries) => {
        bundlerInView = entries[0].isIntersecting;
        updateVisibility();
      }, { threshold: 0.1 });
      bundlerObserver.observe(this.container);

      const externalCtas = document.querySelectorAll('[data-bundler-cta], [data-loyalty-cta]');
      if (externalCtas.length > 0) {
        const ctaObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            entry.isIntersecting ? visibleCtas.add(entry.target) : visibleCtas.delete(entry.target);
          });
          updateVisibility();
        }, { threshold: 0.1 });
        externalCtas.forEach(cta => ctaObserver.observe(cta));
      }

      // Pin the sticky bar to the bottom of the VISUAL viewport, not the layout
      // viewport. On iOS the layout viewport is fixed at the "toolbars-hidden"
      // height, so bottom:0 goes stale while the bottom tray animates in/out,
      // leaving the bar floating above the true bottom.
      const vv = window.visualViewport;
      if (vv) {
        const positionSticky = () => {
          const overlap = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
          this.stickyButton.style.bottom = overlap + 'px';
        };
        let rafId = null;
        const onViewportChange = () => {
          if (rafId) cancelAnimationFrame(rafId);
          rafId = requestAnimationFrame(positionSticky);
        };
        vv.addEventListener('resize', onViewportChange);
        vv.addEventListener('scroll', onViewportChange);
        positionSticky();
      }
    }
  }

  function initLoyaltyBundlers() {
    document.querySelectorAll('.fc-loyalty-bundler[data-section-id]').forEach(container => {
      if (!container.dataset.initialized) {
        new LoyaltyBundler(container);
        container.dataset.initialized = 'true';
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLoyaltyBundlers);
  } else {
    initLoyaltyBundlers();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const container = e.target.querySelector('.fc-loyalty-bundler[data-section-id]');
    if (container) {
      container.dataset.initialized = '';
      new LoyaltyBundler(container);
      container.dataset.initialized = 'true';
    }
  });
})();
